import type { CurvaDocument } from "@/document/model/document";
import { collectResolvedGuides } from "@/document/model/guides";
import type { Rect } from "@/lib/math/rect";
import type { ObjectSnapGuide } from "./objectSnap";

export function guideSnapCandidates(
  document: CurvaDocument,
  activePageId: string | undefined,
  selectedNodeIds: string[],
): { vertical: number[]; horizontal: number[] } {
  const guides = collectResolvedGuides(document, activePageId, selectedNodeIds);
  return {
    vertical: guides.filter(guide => guide.orientation === "vertical").map(guide => guide.documentPosition),
    horizontal: guides.filter(guide => guide.orientation === "horizontal").map(guide => guide.documentPosition),
  };
}

export function snapBoundsToGuides(
  movingBounds: Rect,
  candidates: { vertical: number[]; horizontal: number[] },
  zoom: number,
): { dx: number; dy: number; guides: ObjectSnapGuide[] } {
  const tolerance = 5 / Math.max(0.0001, zoom);
  const xLines = [
    movingBounds.x,
    movingBounds.x + movingBounds.width / 2,
    movingBounds.x + movingBounds.width,
  ];
  const yLines = [
    movingBounds.y,
    movingBounds.y + movingBounds.height / 2,
    movingBounds.y + movingBounds.height,
  ];

  const xSnap = closestLineSnap(xLines, candidates.vertical, tolerance, "vertical", movingBounds);
  const ySnap = closestLineSnap(yLines, candidates.horizontal, tolerance, "horizontal", movingBounds);

  return {
    dx: xSnap?.delta ?? 0,
    dy: ySnap?.delta ?? 0,
    guides: [xSnap?.guide, ySnap?.guide].filter((guide): guide is ObjectSnapGuide => Boolean(guide)),
  };
}

function closestLineSnap(
  movingLines: number[],
  guidePositions: number[],
  tolerance: number,
  orientation: "vertical" | "horizontal",
  bounds: Rect,
): { delta: number; guide: ObjectSnapGuide } | undefined {
  let best: { delta: number; distance: number; position: number } | undefined;

  for (const movingLine of movingLines) {
    for (const guidePosition of guidePositions) {
      const delta = guidePosition - movingLine;
      const distance = Math.abs(delta);
      if (distance > tolerance) {
        continue;
      }

      if (!best || distance < best.distance) {
        best = { delta, distance, position: guidePosition };
      }
    }
  }

  if (!best) {
    return undefined;
  }

  const span = Math.max(bounds.width, bounds.height, 1) * 4;
  return {
    delta: best.delta,
    guide: orientation === "vertical"
      ? { orientation, position: best.position, from: bounds.y - span, to: bounds.y + bounds.height + span }
      : { orientation, position: best.position, from: bounds.x - span, to: bounds.x + bounds.width + span },
  };
}
